import { useState } from "react";
import { Sparkle, Robot, CheckCircle, Spinner, ClipboardText, Warning } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AiCopilot from "@/components/AiCopilot";

/**
 * Zone "Préconisation IA" — V4.24.3 (Fiabilité > automatisation).
 *
 * Bouton principal : "Copier prompt + snapshot"
 *   Copie un prompt prêt-à-coller + le snapshot business dans le presse-papier.
 *   À coller dans ChatGPT / Claude / Gemini pour obtenir l'analyse stratégique.
 *
 * Bouton secondaire : "Snapshot seul" (export brut pour usage libre)
 *
 * IA-3A (C3) : l'export passe par GET /api/ai/export/pilotage — contexte construit côté serveur,
 * pseudonymisé et minimisé par liste blanche (aucun nom, téléphone, e-mail, note).
 * L'analyse intégrée est assurée par <AiCopilot feature="pilotage" /> (couche IA-1).
 */

const PRECONISATION_PROMPT = `Tu es mon coach stratégique business pour le courtage en crédit immobilier.

Analyse les données Crédit Flow ci-dessous et donne-moi une préconisation actionnable IMMÉDIATE.

Format de réponse OBLIGATOIRE (5 blocs, dans cet ordre, avec ces titres exacts en gras Markdown) :

**1. Diagnostic**
1 phrase. État global du mois (en avance / dans le rythme / en retard) + métrique clé.

**2. Risque principal**
1 phrase. Le risque le plus critique des 7 prochains jours. Cite le dossier ou l'indicateur précis.

**3. Opportunité principale**
1 phrase. Le levier business à activer maintenant (apporteur dormant, dossier mûr, RDV à programmer).

**4. Plan d'action 48h**
3 puces. Verbes à l'impératif. Cible nominative. Délai explicite.

**5. Décision recommandée**
1 phrase. La décision business unique à prendre cette semaine.

CONTRAINTES :
- Réponds en français
- Maximum 15 lignes
- Pas de blabla, pas de théorie, pas de disclaimer
- Verbes d'action concrets (Appelle, Relance, Bloque...)
- Les dossiers apparaissent sous des alias D-xxxxxx et les apporteurs par initiales : cite ces alias tels quels, ne tente jamais de deviner une identité
- Chiffres précis (CA, jours, pourcentages) quand disponibles
- Si une donnée manque, dire "donnée manquante" — ne pas inventer`;

async function _copy(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fallback ↓ */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export default function PreconisationIAZone() {
  const [loadingFull, setLoadingFull] = useState(false);
  const [loadingSnap, setLoadingSnap] = useState(false);
  const [doneMode, setDoneMode] = useState(null); // "full" | "snap" | null
  const [sizeKb, setSizeKb] = useState(null);

  const handleCopyFull = async () => {
    setLoadingFull(true);
    setDoneMode(null);
    try {
      const res = await api.get("/ai/export/pilotage");
      const json = JSON.stringify(res.data.data, null, 2);
      const payload = `${PRECONISATION_PROMPT}\n\n--- DONNÉES CRÉDIT FLOW (pseudonymisées, minimisées) ---\n\n${json}`;
      const ok = await _copy(payload);
      if (!ok) throw new Error("clipboard");
      setSizeKb((payload.length / 1024).toFixed(1));
      setDoneMode("full");
      toast.success("Prompt + snapshot copiés — colle dans ChatGPT / Claude / Gemini");
      setTimeout(() => setDoneMode(null), 4000);
    } catch {
      toast.error("Impossible de générer le prompt");
    } finally {
      setLoadingFull(false);
    }
  };

  const handleCopySnap = async () => {
    setLoadingSnap(true);
    setDoneMode(null);
    try {
      const res = await api.get("/ai/export/pilotage");
      const json = JSON.stringify(res.data.data, null, 2);
      const ok = await _copy(json);
      if (!ok) throw new Error("clipboard");
      setSizeKb((json.length / 1024).toFixed(1));
      setDoneMode("snap");
      toast.success("Snapshot copié");
      setTimeout(() => setDoneMode(null), 4000);
    } catch {
      toast.error("Impossible de générer le snapshot");
    } finally {
      setLoadingSnap(false);
    }
  };

  return (
    <section className="card-base p-5 mb-4" data-testid="preconisation-ia-zone">
      <div className="flex items-center gap-2 mb-3">
        <Robot size={18} weight="fill" color="var(--cf-accent)" />
        <h2 className="font-display text-base md:text-lg font-extrabold text-slate-900">
          Préconisation IA
        </h2>
      </div>

      {/* Bandeau explicatif — IA intégrée désactivée */}
      <div
        className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3.5 py-2.5 mb-3"
        data-testid="preconisation-export-notice"
      >
        <Warning size={16} weight="fill" color="var(--cf-warning)" className="mt-0.5 shrink-0" />
        <div className="text-[12px] text-amber-900 leading-snug">
          <span className="font-extrabold">Export pseudonymisé et minimisé.</span>{" "}
          Les données copiées ne contiennent aucun nom, téléphone, e-mail ni note : les dossiers sont des alias D-xxxxxx, les apporteurs des initiales. Colle-les dans ChatGPT / Claude / Gemini, ou utilise l'assistant intégré ci-dessous.
        </div>
      </div>

      {/* Aperçu du prompt (lecture seule, repliable visuellement par overflow) */}
      <pre
        className="rounded-xl bg-slate-50 border border-slate-200 px-3.5 py-3 text-[11.5px] leading-relaxed text-slate-700 whitespace-pre-wrap font-mono mb-3 max-h-40 overflow-auto"
        data-testid="preconisation-prompt-preview"
      >
        {PRECONISATION_PROMPT}
      </pre>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={handleCopyFull}
          disabled={loadingFull || loadingSnap}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-br from-[color:var(--cf-accent)] to-[color:var(--cf-accent-hover)] text-white text-[13px] font-extrabold shadow-sm hover:brightness-110 disabled:opacity-60 transition-all"
          data-testid="preconisation-copy-full-btn"
        >
          {loadingFull ? (
            <Spinner size={16} weight="bold" className="animate-spin" />
          ) : doneMode === "full" ? (
            <CheckCircle size={16} weight="fill" />
          ) : (
            <ClipboardText size={16} weight="bold" />
          )}
          <span>
            {doneMode === "full" ? `Copié (${sizeKb} KB)` : "Copier prompt + snapshot"}
          </span>
        </button>

        <button
          type="button"
          onClick={handleCopySnap}
          disabled={loadingSnap || loadingFull}
          className="sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 text-[13px] font-extrabold hover:bg-slate-50 disabled:opacity-60 transition-all"
          data-testid="preconisation-copy-snap-btn"
        >
          {loadingSnap ? (
            <Spinner size={14} weight="bold" className="animate-spin" />
          ) : doneMode === "snap" ? (
            <CheckCircle size={14} weight="fill" color="var(--cf-success)" />
          ) : (
            <Sparkle size={14} weight="fill" color="var(--cf-primary)" />
          )}
          <span>
            {doneMode === "snap" ? `Copié (${sizeKb} KB)` : "Snapshot seul"}
          </span>
        </button>
      </div>
      {/* IA-1 — analyse directement dans Crédit Flow (OpenAI / Claude), le copier-coller reste disponible */}
      <AiCopilot feature="pilotage" />
    </section>
  );
}
